import { useState, useEffect, type FormEvent, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

// ── Password Reset Flow ──────────────────────────────────────────────────────
//
// Supabase email templates can send two token formats:
//   1. {{ .ConfirmationURL }} — redirects with full JWT in hash fragment:
//      #access_token=eyJ...&refresh_token=...&type=recovery
//   2. {{ .Token }} — redirects with short OTP token_hash in hash:
//      #token=abc123&type=recovery
//
// For most reliable handling, configure the Supabase email template to use
// {{ .ConfirmationURL }}. This code handles BOTH formats for compatibility.

export default function ResetPassword(): JSX.Element {
  const navigate = useNavigate();
  const [password, setPassword] = useState<string>('');
  const [confirm, setConfirm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState<boolean>(false);
  const [invalid, setInvalid] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || !hash.includes('type=recovery')) {
      const search = window.location.search;
      if (!search.includes('type=recovery')) {
        setInvalid(true);
        return;
      }
    }

    const params = new URLSearchParams(
      hash.startsWith('#') ? hash.substring(1) : window.location.search.substring(1)
    );
    const type = params.get('type');
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const tokenHash = params.get('token_hash') || params.get('token');

    if (type !== 'recovery') {
      setInvalid(true);
      return;
    }

    // Format 1: Full JWT access_token in hash (from {{ .ConfirmationURL }})
    if (accessToken) {
      supabase.auth
        .setSession({
          access_token: accessToken,
          refresh_token: refreshToken || '',
        })
        .then(({ error: err }) => {
          if (err) {
            setError(err.message);
            setInvalid(true);
          } else {
            setReady(true);
          }
        });
      return;
    }

    // Format 2: Short OTP token_hash (from {{ .Token }})
    if (tokenHash) {
      supabase.auth
        .verifyOtp({
          token_hash: tokenHash,
          type: 'recovery',
        })
        .then(({ error: err }) => {
          if (err) {
            setError(err.message);
            setInvalid(true);
          } else {
            setReady(true);
          }
        });
      return;
    }

    // Neither format found
    setInvalid(true);
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (err) {
      setError(err.message);
      return;
    }

    setSuccess(true);
    setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
  }

  if (invalid) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center">
          <div className="text-ink-muted text-sm mb-4">
            {error || 'This link is invalid or expired.'}
          </div>
          <a href="/login" className="text-xs text-accent hover:text-accent-hover">
            Back to sign in
          </a>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center">
          <div className="text-ink-muted text-sm">Verifying reset link…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-0 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
            <span className="text-white font-bold text-lg">A</span>
          </div>
          <div>
            <div className="text-xl font-semibold text-ink-bright">Arbi</div>
            <div className="text-xs text-ink-muted">Profitability Decision Engine</div>
          </div>
        </div>

        <div className="arbi-card p-6 space-y-4">
          <h1 className="text-base font-semibold text-ink-bright text-center">Reset password</h1>

          {error && (
            <div className="p-3 rounded-md bg-danger/10 border border-danger/30 text-xs text-danger">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 rounded-md bg-success/10 border border-success/30 text-xs text-success">
              Password updated. Redirecting to dashboard…
            </div>
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-1">
                <label htmlFor="reset-password" className="text-xs text-ink-muted">
                  New password
                </label>
                <input
                  id="reset-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="arbi-input w-full"
                  placeholder="••••••••"
                  required
                  minLength={8}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="reset-confirm" className="text-xs text-ink-muted">
                  Confirm password
                </label>
                <input
                  id="reset-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="arbi-input w-full"
                  placeholder="••••••••"
                  required
                  minLength={8}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="arbi-btn-primary w-full justify-center py-2 mt-1 disabled:opacity-60"
              >
                {loading ? 'Updating…' : 'Set new password'}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-ink-muted mt-4">Access is by invitation only.</p>
      </div>
    </div>
  );
}
